/**
 * HTTP Client for API requests
 * Uses native fetch API with error handling and caching
 */

import { apiCache } from '@/utils/cache';

interface ApiResponse<T> {
  isOk: boolean;
  status: number;
  data?: T;
  message?: string;
  error?: string;
}

class HttpClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  /**
   * Perform a GET request
   */
  async get<T>(endpoint: string, useCache = true): Promise<T> {
    const fullUrl = `${this.baseURL}${endpoint}`;

    // Check cache first
    if (useCache) {
      const cached = apiCache.get<T>(fullUrl);
      if (cached) {
        console.log(`[Cache HIT] ${endpoint}`);
        return cached;
      }
    }

    try {
      console.log(`[API] GET ${endpoint}`);
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const json: ApiResponse<T> = await response.json();

      if (!json.isOk || !json.data) {
        throw new Error(json.message || 'API request failed');
      }

      // Cache the successful response
      if (useCache) {
        apiCache.set(fullUrl, json.data);
        console.log(`[Cache SET] ${endpoint}`);
      }

      return json.data;
    } catch (error) {
      console.error(`[API Error] ${endpoint}:`, error);

      // Check if it's a network error (server down)
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to server. Please check if the backend is running.');
      }

      throw error;
    }
  }

  /**
   * Perform a POST request
   */
  async post<T>(endpoint: string, data: unknown): Promise<T> {
    const fullUrl = `${this.baseURL}${endpoint}`;

    try {
      console.log(`[API] POST ${endpoint}`, data);
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const json: ApiResponse<T> = await response.json();

      if (!json.isOk) {
        throw new Error(json.message || 'API request failed');
      }

      return json.data as T;
    } catch (error) {
      console.error(`[API Error] ${endpoint}:`, error);

      // Check if it's a network error (server down)
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to server. Please check if the backend is running.');
      }

      throw error;
    }
  }
}

// Create a singleton instance
export const httpClient = new HttpClient(__API_BASE_URL__);
